import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, RouteProps } from "react-router-dom";
import { Permission } from "src/models";
import {
  selectCurrentAccessToken,
  selectCurrentUser,
} from "../store/reducers/auth/authSlice";
import NoAccess from "./components/NoAccess/NoAccess";
import { usePermissions } from "./hooks/usePermissions";
import MainLayout from "./layout/MainLayout";
import { allowedRoles, checkRoles } from "./utils/permissionsHelpers";

interface PrivateRouteProps extends RouteProps {
  children: ReactNode;
  /**
   * The permission the page needs, or a list of which any one is enough. Without it the route
   * renders `NoAccess` inside the layout, whether reached by navigation or by typing the address
   * (FR-047).
   */
  permission?:
    | Pick<Permission, "action" | "subject">
    | readonly Pick<Permission, "action" | "subject">[];
}

const PrivateRoute = ({ children, permission, ...rest }: PrivateRouteProps) => {
  const accessToken = useSelector(selectCurrentAccessToken);
  const user = useSelector(selectCurrentUser);
  const { hasPermissions } = usePermissions();
  const required = permission
    ? Array.isArray(permission)
      ? permission
      : [permission]
    : [];
  return (
    <Route
      {...rest}
      render={() => {
        if (!accessToken || !checkRoles(allowedRoles, user?.roles))
          return <Redirect to="/grading" />;
        if (
          required.length > 0 &&
          !required.some((p) => hasPermissions(p.action, p.subject))
        )
          return (
            <MainLayout>
              <NoAccess />
            </MainLayout>
          );
        return children;
      }}
    />
  );
};

export default PrivateRoute;
